/**
 * `/gallery/confirm-gate` exists so a browser can measure the confirm gate's
 * states, because `tests/e2e-harness/src/seed.ts` seeds no pool and no
 * contest, so no harness run reaches a real `pendingOutcome`. This is what
 * makes ISSUE-484's deferred D9 gradeable for the first time.
 *
 * ISSUE-854 corrected the old wording here, which said no harness run could
 * "ever" produce one. `pendingOutcome` is a reachable status now:
 * `contest.detectContestEnd` writes it the moment it opens a pending outcome.
 * What still keeps this route useful is only the harness seed, not the status.
 *
 * ISSUE-891. The outstanding round sections below take their event ids from
 * `/feed/upcoming`, over the leagues `/league-catalog/get` returns, which is
 * the same pair of reads the scores home makes. A spec no longer needs an
 * `/event/detail-get` intercept to visit this route, because those ids
 * resolve against a real events-api. The environment decides how many rows
 * paint, so a harness run with one seeded game paints one row per outstanding
 * section. Where a real contest's `eventIds` come from is still covered by
 * nothing in a browser, which is ISSUE-891's Gap 2 and needs its own card.
 *
 * It is signed in and linked from no navigation, following the
 * `gallery_.marks` and `gallery_.neutral-surfaces` precedent, including the
 * underscore trap: the file is named `gallery_.confirm-gate` so this route
 * stays out of `/gallery`'s tree. `/gallery` renders no `<Outlet />`, so a
 * nested child route would never paint.
 */
import { useFeedUpcoming } from "@sportsmet/events-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { Button } from "@sportsmet/ui";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { ConfirmGateNamingStep } from "../features/contestConfirm/ConfirmGateNamingStep";
import { ConfirmGateOutcomeStep } from "../features/contestConfirm/ConfirmGateOutcomeStep";
import type { typeConfirmGateEntrant } from "../features/contestConfirm/confirmGateState";
import type { typeAutoConfirmStatus } from "../features/contestConfirm/confirmGateStatus";
import { ContestAutoConfirmedNotice } from "../features/contestConfirm/ContestAutoConfirmedNotice";
import { ContestConfirmGateDialog } from "../features/contestConfirm/ContestConfirmGateDialog";
import { PoolHomeConfirmCard } from "../features/contestConfirm/PoolHomeConfirmCard";
import { resolveViewerTimeZone } from "../features/scores/viewerTimeZone";

const ENTRANT_1: typeConfirmGateEntrant = {
  entryId: "e1",
  displayName: "Marcus Webb",
};
const ENTRANT_2: typeConfirmGateEntrant = {
  entryId: "e2",
  displayName: "Dana Ruiz",
};
const ENTRANT_3: typeConfirmGateEntrant = {
  entryId: "e3",
  displayName: "Priya Nair",
};

const MEDAL_NAME = "Survivor medal";

// The first two match what the scores home asks `/feed/upcoming` for, so this
// route inherits a window that already returns the games that feed has. The
// third caps how many rows the multi game section paints.
const GALLERY_FEED_PER_LEAGUE_LIMIT = 5;
const GALLERY_FEED_WINDOW_DAYS = 1;
const GALLERY_MAX_OUTSTANDING_GAMES = 3;

const DIALOG_STATUS: typeAutoConfirmStatus = {
  message: "We'll confirm this for you in 7 days if you don't.",
  tone: "quiet",
  daysUntilAutoConfirm: 7,
};

const ConfirmGateGallery = (): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const catalog = useLeagueCatalogGet({});
  const leagueIds = (catalog.data?.data?.sports ?? []).flatMap((sport) =>
    sport.leagues.map((league) => league.leagueId),
  );
  const feed = useFeedUpcoming(
    {
      leagueIds,
      perLeagueLimit: GALLERY_FEED_PER_LEAGUE_LIMIT,
      windowDays: GALLERY_FEED_WINDOW_DAYS,
      timeZone,
    },
    { enabled: leagueIds.length > 0 },
  );
  // Deduplicated because a repeated event id would be a repeated React key on
  // the row list, which logs a console error, which the e2e page health
  // fixture counts as an unhealthy page.
  const feedEventIds = Array.from(
    new Set(
      (feed.data?.data?.sections ?? []).flatMap((section) =>
        section.events.map((event) => event.eventId),
      ),
    ),
  );
  const outstandingEventIds = feedEventIds.slice(
    0,
    GALLERY_MAX_OUTSTANDING_GAMES,
  );
  const oneOutstandingEventId = feedEventIds.slice(0, 1);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);

  return (
    <main className="flex flex-col gap-8 p-4 pb-10">
      <section
        className="flex flex-col gap-3"
        data-testid="gallery-outcome-single-winner"
      >
        <h2 className="text-body font-bold text-ink">
          Outcome step single winner
        </h2>
        <ConfirmGateOutcomeStep
          shape="singleWinner"
          winners={[ENTRANT_1]}
          medalName={MEDAL_NAME}
          isSeasonLastContest={false}
          namingWindowWarning={null}
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-outcome-split-medal"
      >
        <h2 className="text-body font-bold text-ink">
          Outcome step split medal
        </h2>
        <ConfirmGateOutcomeStep
          shape="splitMedal"
          winners={[ENTRANT_1, ENTRANT_2, ENTRANT_3]}
          medalName={MEDAL_NAME}
          isSeasonLastContest={false}
          namingWindowWarning={null}
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-outcome-no-winner"
      >
        <h2 className="text-body font-bold text-ink">Outcome step no winner</h2>
        <ConfirmGateOutcomeStep
          shape="noWinner"
          winners={[]}
          medalName={MEDAL_NAME}
          isSeasonLastContest={false}
          namingWindowWarning={null}
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-outcome-season-last"
      >
        <h2 className="text-body font-bold text-ink">
          Outcome step season last contest
        </h2>
        <ConfirmGateOutcomeStep
          shape="singleWinner"
          winners={[ENTRANT_1]}
          medalName={MEDAL_NAME}
          isSeasonLastContest={true}
          namingWindowWarning={null}
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-outcome-naming-window"
      >
        <h2 className="text-body font-bold text-ink">
          Outcome step naming window open
        </h2>
        <ConfirmGateOutcomeStep
          shape="splitMedal"
          winners={[ENTRANT_1, ENTRANT_2]}
          medalName={MEDAL_NAME}
          isSeasonLastContest={false}
          namingWindowWarning="Confirming locks these winners for good. You have 31 hours left to change them if you wait."
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-outcome-outstanding-round"
      >
        <h2 className="text-body font-bold text-ink">
          Outcome step outstanding round named
        </h2>
        <ConfirmGateOutcomeStep
          shape="singleWinner"
          winners={[ENTRANT_1]}
          medalName={MEDAL_NAME}
          isSeasonLastContest={false}
          namingWindowWarning={null}
          outstandingRound={
            feedEventIds.length === 0
              ? null
              : { roundId: "round-gallery-1", eventIds: outstandingEventIds }
          }
          outstandingRoundLabel="Sep 3-7"
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-outcome-outstanding-round-one-game"
      >
        <h2 className="text-body font-bold text-ink">
          Outcome step outstanding round unnamed, one game
        </h2>
        <ConfirmGateOutcomeStep
          shape="singleWinner"
          winners={[ENTRANT_1]}
          medalName={MEDAL_NAME}
          isSeasonLastContest={false}
          namingWindowWarning={null}
          outstandingRound={
            feedEventIds.length === 0
              ? null
              : { roundId: "round-gallery-2", eventIds: oneOutstandingEventId }
          }
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-outcome-outstanding-round-named-one-game"
      >
        <h2 className="text-body font-bold text-ink">
          Outcome step outstanding round named, one game
        </h2>
        <ConfirmGateOutcomeStep
          shape="singleWinner"
          winners={[ENTRANT_1]}
          medalName={MEDAL_NAME}
          isSeasonLastContest={false}
          namingWindowWarning={null}
          outstandingRound={
            feedEventIds.length === 0
              ? null
              : { roundId: "round-gallery-3", eventIds: oneOutstandingEventId }
          }
          outstandingRoundLabel="Sep 6"
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-naming-step-empty"
      >
        <h2 className="text-body font-bold text-ink">
          Naming step nothing checked
        </h2>
        <ConfirmGateNamingStep
          candidates={[ENTRANT_1, ENTRANT_2, ENTRANT_3]}
          onSave={() => undefined}
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-card-single-winner"
      >
        <h2 className="text-body font-bold text-ink">
          Pool home card single winner
        </h2>
        <PoolHomeConfirmCard
          winners={[ENTRANT_1]}
          medalName={MEDAL_NAME}
          daysUntilAutoConfirm={5}
          onReviewAndConfirm={() => undefined}
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-card-split-medal"
      >
        <h2 className="text-body font-bold text-ink">
          Pool home card split medal
        </h2>
        <PoolHomeConfirmCard
          winners={[ENTRANT_1, ENTRANT_2, ENTRANT_3]}
          medalName={MEDAL_NAME}
          daysUntilAutoConfirm={1}
          onReviewAndConfirm={() => undefined}
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-card-no-winner"
      >
        <h2 className="text-body font-bold text-ink">
          Pool home card no winner
        </h2>
        <PoolHomeConfirmCard
          winners={[]}
          medalName={MEDAL_NAME}
          daysUntilAutoConfirm={0}
          onReviewAndConfirm={() => undefined}
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-dialog-outcome"
      >
        <h2 className="text-body font-bold text-ink">
          Gate dialog outcome step
        </h2>
        {/* A local useState is correct here, and only here: this is a
            developer-only gallery toggle, not product overlay state, and
            this route carries no product URL contract. */}
        <Button
          variant="secondary"
          size="md"
          onClick={() => {
            setIsDialogOpen(true);
          }}
        >
          Open the gate dialog
        </Button>
        <ContestConfirmGateDialog
          open={isDialogOpen}
          view={{
            step: "outcome",
            shape: "singleWinner",
            winners: [ENTRANT_1],
            medalName: MEDAL_NAME,
            isSeasonLastContest: false,
            outstandingRound: null,
          }}
          status={DIALOG_STATUS}
          namingWindowWarning={null}
          onConfirm={() => undefined}
          onNotYet={() => {
            setIsDialogOpen(false);
          }}
          onReviveInstead={() => undefined}
          onSaveNamedWinners={() => undefined}
        />
      </section>

      <section
        className="flex flex-col gap-3"
        data-testid="gallery-auto-confirmed-notice"
      >
        <h2 className="text-body font-bold text-ink">Auto confirmed notice</h2>
        <Button
          variant="secondary"
          size="md"
          onClick={() => {
            setIsNoticeOpen(true);
          }}
        >
          Open the auto-confirmed notice
        </Button>
        <ContestAutoConfirmedNotice
          open={isNoticeOpen}
          winnerNames={["Marcus Webb"]}
          medalName={MEDAL_NAME}
          onSeeResults={() => {
            setIsNoticeOpen(false);
          }}
        />
      </section>
    </main>
  );
};

export const Route = createFileRoute("/gallery_/confirm-gate")({
  beforeLoad: requireSignedIn,
  component: ConfirmGateGallery,
});
